import { LinkBreakIcon, LinkSimpleIcon, MapTrifoldIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import {
  CANDIDATE_REASON_LABEL,
  linkTrackToFlight,
  suggestTracksForFlight,
  type TrackCandidate,
  unlinkTrackFromFlight,
} from '@/features/flights/link';
import type { Flight, LogbookEntry } from '@/lib/db';
import { formatLocalDate, formatLocalTime } from '@/lib/format';

import { ApiMatchCell } from './ApiMatchCell';

/**
 * The track link, in the logbook, per row.
 *
 * Every other column here is a cell in the spreadsheet's keyboard grid; this
 * one deliberately is not. A link is not a value a pilot types, fills down or
 * undoes — it is a relationship between two records — so it sits outside
 * `LOGBOOK_COLUMNS` next to the draft/approved control, which is outside for
 * the same reason.
 *
 * An entry whose flight has a track links straight to the map. One whose
 * doesn't offers two ways to get one: the tracks already imported that it
 * might belong to, best match first, and — when a data source is configured —
 * a search of that source for the flight the entry describes
 * (`ApiMatchCell`). Local first, deliberately: it costs nothing, and it is
 * nearly always where the track already is.
 */

export interface TrackLinkCellProps {
  entry: LogbookEntry;
  /** The entry's flight, already fetched by the grid — one query for the page, not one per row. */
  flight: Flight | undefined;
}

export function TrackLinkCell({ entry, flight }: TrackLinkCellProps) {
  const [candidates, setCandidates] = useState<TrackCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);

  const trackId = flight?.trackId;

  /*
   * One `<span>` with a branch inside it, rather than two `return`s with two
   * different trees.
   *
   * `ApiMatchCell` has to keep its own state across the moment an entry
   * *gains* a track: accepting a match is what links one, and its
   * field-by-field diff is still on screen and unanswered when that happens.
   * React reconciles children by position, so the same component sitting at
   * index 2 of one tree and index 1 of another is unmounted and remounted —
   * and the panel vanishes mid-decision. Holding it at a fixed position keeps
   * it alive. Found in the browser; the unit test renders `ApiMatchCell`
   * directly, with a fixed `flight` prop, and could not see it.
   */
  return (
    <span className="relative flex items-center gap-1.5">
      {trackId !== undefined && flight !== undefined ? (
        <>
          <Link
            to="/flights/$trackId"
            params={{ trackId }}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
            title="Open the track this entry was drafted from"
          >
            <MapTrifoldIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-3.5" />
            Track
          </Link>
          <button
            type="button"
            disabled={busy}
            aria-label={`Break the link between the ${entry.date} entry and its track`}
            title="Break the link. The entry keeps every value it has."
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            onClick={() => {
              setBusy(true);
              void unlinkTrackFromFlight(flight.id).finally(() => {
                setBusy(false);
              });
            }}
          >
            <LinkBreakIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-3.5" />
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            disabled={busy || flight === undefined}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs disabled:opacity-50"
            title={
              flight === undefined
                ? 'This entry has no flight record to link a track to.'
                : 'Find an already-imported track'
            }
            onClick={() => {
              if (flight === undefined) {
                return;
              }
              setBusy(true);
              void suggestTracksForFlight(flight, entry.registration, { limit: 8 })
                .then(setCandidates)
                .finally(() => {
                  setBusy(false);
                });
            }}
          >
            <LinkSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-3.5" />
            Link
          </button>

          {candidates === null ? null : (
            <div
              className="border-border bg-surface rounded-container absolute top-full left-0 z-(--z-overlay) mt-1 w-72 space-y-1 border p-2 shadow-lg"
              data-testid="track-picker"
            >
              <p className="text-muted-foreground text-xs">Tracks with no flight, closest first.</p>
              {candidates.length === 0 ? (
                <p className="text-muted-foreground text-xs">
                  Every imported track already belongs to a flight.
                </p>
              ) : (
                <ul>
                  {candidates.map((candidate) => (
                    <li key={candidate.track.id}>
                      <button
                        type="button"
                        disabled={busy || flight === undefined}
                        className="hover:bg-surface-sunken rounded-control w-full px-1.5 py-1 text-left text-xs"
                        onClick={() => {
                          if (flight === undefined) {
                            return;
                          }
                          setBusy(true);
                          void linkTrackToFlight(flight.id, candidate.track.id)
                            .then(() => {
                              setCandidates(null);
                            })
                            .finally(() => {
                              setBusy(false);
                            });
                        }}
                      >
                        <span className="text-foreground block">
                          {formatLocalDate(candidate.track.startTime)}
                          {' · '}
                          <span className="tabular">
                            {formatLocalTime(candidate.track.startTime)}
                          </span>
                          {candidate.track.registration === undefined
                            ? ''
                            : ` · ${candidate.track.registration}`}
                        </span>
                        <span className="text-muted-foreground block">
                          {CANDIDATE_REASON_LABEL[candidate.reason]}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground text-xs"
                onClick={() => {
                  setCandidates(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}

      <ApiMatchCell entry={entry} flight={flight} />
    </span>
  );
}
